import { useRef, useState } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const SILENCIO = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//WreyTRUoAWgBgkOAGbZHBgG1OF6zM82DWbZaUmMBptgQhGjsyYqc9ae9XFz280948NMBWInljyzsNRFLPWdnZGWrddDsjK1unuSrVN9jJsK8KuQtQCtMBjCEtImISdNKJOopIpBFpNSMbIHCSRpRR5iakjTiyzLhchUUBwCgyKiweBv/7UsQbg8isVNoMPMjAAAA0gAAABEVFGmgqK////9bP/6XCykxBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq";

// Un solo elemento de audio compartido: se "desbloquea" dentro del gesto del usuario (iOS) y luego reproduce el TTS.
export const audioEl = () => { if (!window.__copAudio) window.__copAudio = new Audio(); return window.__copAudio; };
export const desbloquearAudio = () => { const a = audioEl(); if (a.dataset.ok) return; a.src = SILENCIO; a.play().then(() => { a.dataset.ok = "1"; }).catch(() => {}); };
export const callar = () => { const a = audioEl(); a.pause(); };

export const hablar = async (texto) => {
  if (!texto) return;
  try {
    const r = await http.post("/copiloto/tts", { texto: texto.slice(0, 600) }, { responseType: "blob" });
    const a = audioEl();
    if (a.dataset.url) URL.revokeObjectURL(a.dataset.url);
    a.dataset.url = URL.createObjectURL(r.data);
    a.src = a.dataset.url;
    await a.play();
  } catch { /* la voz es opcional: el texto ya está en pantalla */ }
};

const tipoMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
};

export const useVoz = (onTexto, onError) => {
  const [estado, setEstado] = useState("idle");
  const [seg, setSeg] = useState(0);
  const rec = useRef(null);
  const t0 = useRef(0);
  const timers = useRef([]);
  const limpiar = () => { timers.current.forEach(clearTimeout); timers.current.forEach(clearInterval); timers.current = []; };
  const detener = () => { try { if (rec.current?.state === "recording") rec.current.stop(); } catch { /* ya detenido */ } };
  const iniciar = async () => {
    if (estado !== "idle") return;
    callar(); desbloquearAudio();
    const mime = tipoMime();
    if (mime === null || !navigator.mediaDevices?.getUserMedia) { onError("Este navegador no permite grabar audio."); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch (e) {
      onError(e?.name === "NotAllowedError" ? "Permiso de micrófono denegado. Actívalo en la configuración del navegador para hablar con el copiloto." : "No se encontró un micrófono disponible.");
      return;
    }
    const chunks = [];
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    rec.current = mr;
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = async () => {
      limpiar(); stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: (mr.mimeType || mime || "audio/webm").split(";")[0] });
      if (blob.size < 2000) { setEstado("idle"); onError("No se escuchó audio. Mantén presionado o toca y habla."); return; }
      setEstado("transcribiendo");
      try {
        const fd = new FormData();
        fd.append("audio", blob, `voz.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
        const r = await http.post("/copiloto/stt", fd);
        onTexto(r.data.texto);
      } catch (e) { onError(errMsg(e)); }
      setEstado("idle");
    };
    mr.start(250);
    t0.current = Date.now(); setSeg(0); setEstado("grabando");
    timers.current.push(setInterval(() => setSeg(Math.floor((Date.now() - t0.current) / 1000)), 250), setTimeout(detener, 60000));
  };
  // toca para iniciar/detener, o mantén presionado y suelta
  const down = () => (estado === "grabando" ? detener() : iniciar());
  const up = () => { if (estado === "grabando" && Date.now() - t0.current > 600) detener(); };
  return { estado, seg, down, up };
};

export const MicButton = ({ voz }) => (
  <button type="button" onPointerDown={voz.down} onPointerUp={voz.up} disabled={voz.estado === "transcribiendo"} data-testid="ai-mic" data-estado={voz.estado}
    title={voz.estado === "grabando" ? "Detener" : "Hablar (máx. 60 s)"} aria-label="Micrófono"
    className={`relative grid h-10 w-10 shrink-0 place-items-center rounded-full transition-colors ${voz.estado === "grabando" ? "bg-rose-600 text-white" : "border border-[#E2DDD5] text-[#13315C] hover:bg-[#F2F0EC]"}`}>
    {voz.estado === "grabando" && <span className="absolute inset-0 animate-ping rounded-full bg-rose-500/40" />}
    {voz.estado === "transcribiendo" ? <Loader2 size={16} className="animate-spin" /> : voz.estado === "grabando" ? <Square size={14} /> : <Mic size={17} />}
  </button>
);
